import type { ReactNode } from 'react';
import { XIcon } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@design-system';
import CompanyLogoContainer from '@/components/CompanyLogo';
import { ClientUserMenu } from '@/components/ClientUserMenu';
import { ClientDataModal } from '@/components/ClientDataModal';
import { ContactAdvisorButton } from '@/components/ContactAdvisorButton';
import { RequestAdvisorMeetingModal } from '@/components/RequestAdvisorMeetingModal';
import { IntakeFormActionPilotRestricted } from '@/shared/components/pilot-restricted';
import { useIsConsumerFlowEnabled } from '@/hooks/use-app-mode';
import { useOnboardingHeader } from '../hooks/use-onboarding-header';

interface OnboardingBrandHeaderProps {
  progress?: ReactNode;
  extraActions?: ReactNode;
}

/** The client-plan header: branding, progress and the advisor/account actions. */
export function OnboardingBrandHeader({ progress, extraActions }: OnboardingBrandHeaderProps) {
  const header = useOnboardingHeader();
  const isConsumerFlow = useIsConsumerFlowEnabled();
  // Consumers in the guided flow have no advisor of their own to connect with.
  const showContactAdvisor = header.isClient && !isConsumerFlow;

  const intakeFormButtonInner = (
    <IntakeFormButton
      tooltip={header.intakeFormTooltip}
      onClick={header.onIntakeFormClick}
      disabled={header.isClient && header.isLoadingForm}
      isLoading={header.isLoadingForm}
      buttonText={header.buttonText}
    />
  );

  const intakeFormButton = header.showIntakeFormButton && (
    <div className="hidden sm:block">
      <IntakeFormActionPilotRestricted
        client={header.client}
        isClient={header.isClient}
      >
        {intakeFormButtonInner}
      </IntakeFormActionPilotRestricted>
    </div>
  );

  return (
    <>
      <div className="bg-background border-b p-4 md:px-6">
        <div className="flex flex-col lg:grid lg:grid-cols-[1fr_minmax(max-content,64rem)_1fr] lg:items-center lg:gap-6">
          <div className="mb-3 flex items-center justify-between lg:contents">
            <CompanyLogoContainer
              organizationDisplayName={header.advisor.organizationDisplayName}
              advisorLogoKey={header.advisor.advisorLogoKey}
              brandingPreference={header.advisor.whitelabelBrandingPreference}
              onClick={header.isClient ? undefined : header.navigateToClientsDashboard}
            />
            <div className="ml-auto flex items-center gap-2 lg:order-3 lg:justify-self-end">
              {showContactAdvisor && (
                <ContactAdvisorButton
                  iconOnMobile
                  requestMeetingOnClick={header.openRequestMeeting}
                />
              )}
              {intakeFormButton}
              {extraActions}
              {header.isClient ? (
                <ClientUserMenu
                  handleClickSignOut={header.handleSignOut}
                  clientEmail={header.clientEmail}
                  onNavigateHome={header.navigateToClientHome}
                  showEmail={false}
                />
              ) : (
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="size-9 rounded-full md:size-10"
                  onClick={header.navigateToClientsDashboard}
                  aria-label="Close and return to dashboard"
                >
                  <XIcon />
                </Button>
              )}
            </div>
          </div>
          {progress && <div className="lg:order-2">{progress}</div>}
        </div>
      </div>

      <RequestAdvisorMeetingModal
        open={header.isRequestMeetingOpen}
        onClose={header.closeRequestMeeting}
      />
      {!header.isClient && header.client && (
        <ClientDataModal
          partialClient={header.client}
          open={header.isQuickEditOpen}
          initialMode="edit"
          onClose={header.closeQuickEdit}
        />
      )}
    </>
  );
}

function IntakeFormButton({
  tooltip,
  onClick,
  disabled,
  isLoading,
  buttonText,
}: {
  tooltip: string;
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
  buttonText: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Button
            onClick={onClick}
            disabled={disabled}
            variant="outline"
            className="h-9 rounded-full md:h-10"
            data-testid="update-intake-form-button"
          >
            {isLoading ? 'Loading...' : buttonText}
          </Button>
        </TooltipTrigger>
        <TooltipContent
          align="end"
          side="bottom"
          className="max-w-xs"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
